import {
  Alert,
  Anchor,
  Badge,
  Card,
  Group,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import dayjs from 'dayjs';
import {
  TShopStatus,
  useShopDisable,
  useShopEnable,
} from '@/models/commercial-proposal';
import { PERMISSIONS, useHasPermission } from '@/models/permissions';

function StatusBadge({ shop }: { shop: TShopStatus }) {
  if (shop.lastError) {
    return (
      <Tooltip label={shop.lastError} multiline maw={320}>
        <Badge color="red" variant="light">
          Delivery failing
        </Badge>
      </Tooltip>
    );
  }
  if (shop.pending) {
    return (
      <Badge color="yellow" variant="light">
        Updating
      </Badge>
    );
  }
  if (shop.deliveredRevision) {
    return (
      <Badge color="green" variant="light">
        Up to date
      </Badge>
    );
  }
  return (
    <Badge color="gray" variant="light">
      Not delivered yet
    </Badge>
  );
}

export default function ShopPanel({
  proposalId,
  isDraft,
  shop,
}: {
  proposalId: number;
  isDraft: boolean;
  shop: TShopStatus | null | undefined;
}) {
  const canPublish = useHasPermission(PERMISSIONS.PROPOSALS_PUBLISH);
  const enable = useShopEnable(proposalId);
  const disable = useShopDisable(proposalId);
  const enabled = !!shop;
  const busy = enable.isPending || disable.isPending;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      enable.mutate();
      return;
    }
    const count = shop?.usedOn.length ?? 0;
    if (count === 0) {
      disable.mutate();
      return;
    }
    modals.openConfirmModal({
      title: 'Disable shop availability',
      children: (
        <Text size="sm">
          This proposal is used on {count} shop page(s). They will show it as
          unavailable until it is enabled again.
        </Text>
      ),
      labels: { confirm: 'Disable', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => disable.mutate(),
    });
  };

  const disabledReason = !canPublish
    ? 'You do not have permission to change shop availability'
    : isDraft && !enabled
      ? 'Publish the proposal first'
      : null;

  return (
    <Card withBorder padding="sm" mb="md" className="no-block-deselect">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Tooltip label={disabledReason} disabled={!disabledReason}>
            <div>
              <Switch
                label="Available for shop"
                checked={enabled}
                disabled={!!disabledReason || busy}
                onChange={(e) => handleToggle(e.currentTarget.checked)}
              />
            </div>
          </Tooltip>
          {shop && <StatusBadge shop={shop} />}
        </Group>
        {isDraft && enabled && (
          <Alert color="yellow" p="xs">
            <Text size="xs">Draft proposals cannot be sold on the shop.</Text>
          </Alert>
        )}
        {shop && (
          <>
            {shop.deliveredAt && (
              <Text size="xs" c="dimmed">
                Last delivered:{' '}
                {dayjs(shop.deliveredAt).format('DD.MM.YYYY HH:mm:ss')}
              </Text>
            )}
            <Stack gap={2}>
              <Text size="xs" fw={500}>
                Used on shop
              </Text>
              {shop.usedOn.length === 0 ? (
                <Text size="xs" c="dimmed">
                  Not placed on any shop page yet
                </Text>
              ) : (
                shop.usedOn.map((url) =>
                  url.startsWith('https://') ? (
                    <Anchor
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      size="xs"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {url}
                    </Anchor>
                  ) : (
                    <Text
                      key={url}
                      size="xs"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {url}
                    </Text>
                  ),
                )
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
}
